import { useState, type ReactNode } from 'react';
import { Icon } from './Icon.js';

/**
 * − value + for one number setting. The value is an <output> (provisional while a command is in flight). With onType a
 * tap on the value opens a number field (D-G04 of APP-PARITY.md "Detail-Abgleich", stepCtl() of the local app): Enter
 * or leaving the field sends what was typed, Escape keeps the value.
 */
export function Stepper({
  value,
  provisional = false,
  disabled = false,
  onMinus,
  onPlus,
  minusLabel,
  plusLabel,
  minusTestId,
  plusTestId,
  valueTestId,
  onType,
  editValue,
  typeLabel,
}: {
  value: ReactNode;
  provisional?: boolean;
  disabled?: boolean;
  onMinus: () => void;
  onPlus: () => void;
  minusLabel: string;
  plusLabel: string;
  minusTestId?: string;
  plusTestId?: string;
  valueTestId?: string;
  /** a typed value (text as typed, "93,5"); without it the value is not typeable */
  onType?: (text: string) => void;
  /** the number in the field when it opens, without its unit */
  editValue?: string;
  /** label of the field and the value's tooltip ("Wert eingeben") */
  typeLabel?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const typeable = !!onType && !disabled;
  const open = () => {
    setText(editValue ?? '');
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    if (text.trim() && text.trim() !== (editValue ?? '').trim()) onType?.(text);
  };
  return (
    <div className="stepper">
      <button
        type="button"
        className="stepper-btn"
        aria-label={minusLabel}
        disabled={disabled}
        onClick={onMinus}
        data-testid={minusTestId}
      >
        <Icon name="minus" />
      </button>
      {editing && typeable ? (
        <input
          className="stepper-input"
          inputMode="decimal"
          // the field takes the focus it was opened for (a tap on the value)
          autoFocus
          aria-label={typeLabel}
          value={text}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setText(editValue ?? '');
              setEditing(false);
            }
          }}
          data-testid={valueTestId ? `${valueTestId}-input` : undefined}
        />
      ) : typeable ? (
        <button
          type="button"
          className={provisional ? 'stepper-value stepper-type provisional' : 'stepper-value stepper-type'}
          title={typeLabel}
          onClick={open}
          data-testid={valueTestId}
        >
          {value}
        </button>
      ) : (
        <output
          className={provisional ? 'stepper-value provisional' : 'stepper-value'}
          data-testid={valueTestId}
        >
          {value}
        </output>
      )}
      <button
        type="button"
        className="stepper-btn"
        aria-label={plusLabel}
        disabled={disabled}
        onClick={onPlus}
        data-testid={plusTestId}
      >
        <Icon name="plus" />
      </button>
    </div>
  );
}
